import { ChangeEvent, FC, useCallback, useEffect, useState } from "react"
import classNames from "classnames"
import { useDispatch, useSelector } from "react-redux"
import { soundCheck } from "@/components/AlertSoundSettings/files"
import AuthLayout from "@/components/AuthLayout"
import LoginSupport from "@/components/LoginSupport"
import Button, { ButtonIconPosition } from "@/components/shared/Button"
import Link, { LinkVariant } from "@/components/shared/Link"
import TextInput from "@/components/shared/TextInput"
import { TestId } from "@/constants/globals"
import { PageRoutes } from "@/constants/routes"
import useStateHandler from "@/hooks/useStateHandler"
import { AppDispatch, RootState } from "@/store"
import { entraLogin, login } from "@/store/authSlice"
import { Variant } from "@/types/index"
import { getErrorMessage } from "@/utils/index"
import { isEmpty, isNull } from "@/utils/lodash"
import "./index.scss"

const Login: FC = () => {
   const dispatch = useDispatch<AppDispatch>()
   const { loadingLogin, errorLogin, loadingEntraLogin, errorEntraLogin } =
      useSelector(({ authReducer }: RootState) => authReducer)
   const [error, setError] = useState(!isNull(errorLogin))
   const [state, setState] = useStateHandler({ email: "", password: "" })
   const [soundPlaying, setSoundPlaying] = useState(false)
   const { email, password } = state
   const isFormValid = !loadingLogin && !isEmpty(email) && !isEmpty(password)

   const notificationPermission = globalThis.Notification?.permission

   const handleCheckSound = useCallback(() => {
      const audio = new Audio(soundCheck)
      setSoundPlaying(true)
      audio.play().catch(() => setSoundPlaying(false))
      audio.onended = () => setSoundPlaying(false)
      audio.onerror = () => setSoundPlaying(false)
   }, [])

   const handleLogin = useCallback(async () => {
      try {
         await dispatch(login({ email, password })).unwrap()
      } catch (err) {
         console.error("Login failed:", err)
      }
   }, [dispatch, email, password])

   const handleEntraLogin = useCallback(async () => {
      try {
         const authorizeUrl = await dispatch(entraLogin()).unwrap()
         window.location.assign(authorizeUrl)
      } catch (err) {
         console.error("Microsoft login failed:", err)
      }
   }, [dispatch])

   const handleKeyDown = useCallback(
      (e: KeyboardEvent) => {
         if (e.key === "Enter" && isFormValid) {
            handleLogin()
         }
      },
      [isFormValid, handleLogin]
   )

   const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      setState(e)
      if (error) {
         setError(false)
      }
   }

   useEffect(() => {
      setError(!isNull(errorLogin))
   }, [errorLogin])

   useEffect(() => {
      window.addEventListener("keydown", handleKeyDown)
      return () => {
         window.removeEventListener("keydown", handleKeyDown)
      }
   }, [email, password, loadingLogin, handleKeyDown])

   return (
      <AuthLayout
         iconName="guardUser"
         title="User login"
         instructions={
            <>
               <div className="login__instructions-description">
                  Please enter in your email address and your password to login.
                  Forgot your password?&nbsp;
                  <Link
                     className="login__instructions-link"
                     to={PageRoutes.PASSWORD_RECOVERY}
                     variant={LinkVariant.SECONDARY}
                     underline
                  >
                     Click here to recover it.
                  </Link>
               </div>
               <LoginSupport />
            </>
         }
      >
         <>
            <TextInput
               name="email"
               variant={Variant.LARGE}
               disabled={loadingLogin}
               error={error}
               className="login__form-input"
               placeholder="Your email address"
               value={email}
               testId={TestId.LOGIN_EMAIL_INPUT}
               onChange={handleChange}
            />
            <TextInput
               name="password"
               variant={Variant.LARGE}
               type="password"
               disabled={loadingLogin}
               error={error}
               className="login__form-input"
               placeholder="Your password"
               value={password}
               testId={TestId.LOGIN_PASSWORD_INPUT}
               onChange={handleChange}
            />
            <Button
               disabled={!isFormValid}
               className="login__form-input"
               onClick={handleLogin}
               data-testid={TestId.LOGIN_LOGIN_BUTTON}
            >
               Login
            </Button>
            <Link to={PageRoutes.PASSWORD_RECOVERY}>
               Forgot your login information?
            </Link>
            <div className="login__delimitator">or</div>
            <Button
               className="login__microsoft-button"
               iconName="microsoft"
               iconPosition={ButtonIconPosition.LEFT}
               disabled={loadingEntraLogin}
               onClick={handleEntraLogin}
            >
               Sign in with Microsoft
            </Button>
            <div
               className="login__error"
               data-testid={TestId.LOGIN_ERROR_MESSAGE}
            >
               {errorEntraLogin ||
                  (errorLogin &&
                     getErrorMessage(
                        errorLogin,
                        "Incorrect information, please check your password and account name and try again"
                     ))}
            </div>
            <div className="login__permissions">
               <Button
                  iconName="speaker"
                  iconPosition={ButtonIconPosition.LEFT}
                  onClick={handleCheckSound}
                  disabled={soundPlaying}
               >
                  {soundPlaying ? "Playing..." : "Check volume"}
               </Button>
               {notificationPermission && (
                  <div className="login__settings">
                     <span>
                        {"Notifications: "}
                        <span
                           className={classNames(
                              "login__permissions-description",
                              `login__permissions-description--${notificationPermission}`
                           )}
                        >
                           {notificationPermission?.toUpperCase() ?? "UNKNOWN"}
                        </span>
                     </span>
                     <span>
                        {"Set browser "}
                        <Link
                           to="https://drive.google.com/file/d/1ZPpNzRLYehsyiTVmSWEzcsHaLUsSPoG_/view?usp=drive_link"
                           external
                        >
                           Settings
                        </Link>
                     </span>
                  </div>
               )}
            </div>
         </>
      </AuthLayout>
   )
}

export default Login
